import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  calibrationGuidanceApi,
  type CalibrationGuidance,
} from '../../lib/calibrationGuidanceApi';
import { formatAppDateTime } from '../../lib/localTime';

export function CalibrationGuidancePage() {
  const [data, setData] = useState<CalibrationGuidance | null>(null);
  const [draft, setDraft] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const next = await calibrationGuidanceApi.get();
        setData(next);
        setDraft(next.instructions);
      } catch (err) {
        setLoadError((err as Error).message);
      }
    })();
  }, []);

  const cap = data?.maxChars ?? 8000;
  const dirty = data ? draft !== data.instructions : false;

  const save = async () => {
    setBusy(true);
    setSaveError(null);
    try {
      const next = await calibrationGuidanceApi.save(draft);
      setData(next);
      setDraft(next.instructions);
      setSavedAt(formatAppDateTime(next.updatedAt));
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    if (!window.confirm('Replace the brief with the built-in default?')) return;
    setBusy(true);
    setSaveError(null);
    try {
      const next = await calibrationGuidanceApi.restore();
      setData(next);
      setDraft(next.instructions);
      setSavedAt(formatAppDateTime(next.updatedAt));
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-col" style={{ gap: 14 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>Calibration brief</h1>
        <span className="spacer" />
        <Link to="/settings" className="btn small">
          Back to Settings
        </Link>
      </div>

      <p className="muted small" style={{ marginTop: 0 }}>
        Instructions Look at that follows when you press <strong>Calibrate</strong>.
        This is not a pass/fail test — it is how the bot walks the chrome and
        builds a UI map (hamburger vs autoplay, where history/help live, and so on).
        Saved facts go into the run atlas and game memory for later live plays.
        Edit this whenever a studio&apos;s UI fools the default walk.
      </p>

      {loadError && (
        <div className="card" style={{ borderColor: 'var(--bad)', color: 'var(--bad)' }}>
          {loadError}
        </div>
      )}

      {!data && !loadError && <div className="muted">Loading…</div>}

      {data && (
        <div className="card flex-col" style={{ gap: 10 }}>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <label className="muted small">Calibration instructions</label>
            <span className="spacer" />
            <span className="muted small mono">
              {draft.length}/{cap}
            </span>
          </div>
          <textarea
            value={draft}
            maxLength={cap}
            rows={18}
            disabled={busy}
            onChange={(e) => setDraft(e.target.value)}
            style={{ width: '100%', fontFamily: 'inherit', lineHeight: 1.45 }}
          />
          {saveError && (
            <p className="small" style={{ color: 'var(--bad)', margin: 0 }}>
              {saveError}
            </p>
          )}
          {savedAt && !dirty && (
            <p className="muted small" style={{ margin: 0 }}>
              Saved {savedAt}
            </p>
          )}
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn small btn-primary"
              disabled={busy || !dirty || !draft.trim()}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="btn small"
              disabled={busy || !dirty}
              onClick={() => setDraft(data.instructions)}
            >
              Discard
            </button>
            <span className="spacer" />
            <button
              type="button"
              className="btn small"
              disabled={busy}
              onClick={() => void restore()}
            >
              Restore default
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
